/*****************************************************
 * Packages
 ******************************************************/
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

/*****************************************************
 * Locals
 ******************************************************/
import Label from '../Label/Label';
import Tooltip from '../../Tooltip';
import settingComponents from '../index';
import { getAuthUser } from '../../../redux/selectors';
import getModContent from '../../../util/getModContent';
import validateMembership from '../../../util/validateMembership';

const StyledInput = styled.div`
    margin-bottom: 10px;

    &:last-child {
        margin-bottom: 0;
    }
`;

const LabelWrap = styled.div`
    display: flex;
    margin-top: 20px;
    justify-content: space-between;
`;

function RenderComponent(props) {
    const {
        path,
        value,
        display,
        module,
        onChange,
        activeHover,
        parentModule,
        mqPlaceholder,
    } = props;
    const user = useSelector(getAuthUser);
    const modContent = useMemo(() => getModContent(module), [module]);

    if (!modContent) {
        return;
    }

    const Component = settingComponents[modContent?.template] || 'div';

    if (!path) {
        console.error('No path assigned for: ' + module.label);
        return null;
    }

    // Help message for development ease
    if (!Component) {
        return (
            <p style={{ color: 'red' }}>
                No component found for '{path || modContent?.template}'
            </p>
        );
    }

    let visibleOn = 'normal';
    if (Array.isArray(modContent.visibleOn)) {
        visibleOn = modContent.visibleOn;
    }

    if (activeHover && !visibleOn.includes('hover')) {
        if (parentModule.hasOwnProperty('hoverable')) {
            if (!parentModule.hoverable) {
                return null;
            }
        } else {
            if (!modContent.hoverable) {
                return null;
            }
        }
    }

    const enabled = validateMembership(parentModule.membership, user);
    const defaultValidation = {
        validate: () => {},
        message: '',
    };

    const validation = module?.validation || defaultValidation;
    const isValid = validation.validate(value);

    return (
        <StyledInput>
            {modContent.label && modContent.labelPosition !== 'inline' && (
                <LabelWrap>
                    <Label
                        display={display}
                        module={modContent}
                        activeHover={activeHover}
                    >
                        {modContent.label}
                    </Label>
                    {modContent.info && (
                        <Tooltip
                            effect="hover"
                            placement="top-left"
                            content={modContent.info}
                        >
                            <FontAwesomeIcon
                                fixedWidth
                                icon={['fas', 'info-circle']}
                            />
                        </Tooltip>
                    )}
                </LabelWrap>
            )}

            {enabled ? (
                <>
                    <Component
                        {...modContent}
                        name={path}
                        value={value}
                        onChange={onChange}
                        mqPlaceholder={mqPlaceholder}
                    />
                    {false === isValid && (
                        <div style={{ marginTop: 5, color: 'red' }}>
                            {validation.message}
                        </div>
                    )}
                </>
            ) : (
                <Link to="/dashboard/account/billing" target="_blank">
                    Upgrade to Pro
                </Link>
            )}
        </StyledInput>
    );
}

export default React.memo(RenderComponent);
